import { useState } from 'react'

import { useUnsafeMemo } from '@factorialco/deprecated-design-system'
import {
  PlanningBalancesByPeriodsDocument,
  PlanningBalancesDocument,
  TimePlanningBalance,
  TimePlanningBalancesByPeriod,
  TimePlanningEnumsBalancesOnlyBalancesEnum,
} from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'

import { useCurrentViewOptions } from 'modules/shift_management/hooks/useCurrentViewOptions'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'

const useFetchPlanningBalances = () => {
  const [{ canManageShifts }] = usePermissions()
  const [{ paginatedShiftsRows }] = useShiftsRows()
  const [{ view }] = useSchedule()
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const { viewOptions } = useCurrentViewOptions()
  const { employee } = viewOptions
  const { showPlanningBalance } = employee
  const hasPlanningBalanceEnabled = hasFeatureFlag('dev_time_planning_balances_in_shifts')
  const hasOverPlannedAlertEnabled = hasFeatureFlag('dev_shift_management_over_planned_alert')

  const shouldLoadBalances = hasPlanningBalanceEnabled && showPlanningBalance

  const only = useMemo(() => {
    // Skipping 'shift_hours' for now, calculated in front.
    const result: TimePlanningEnumsBalancesOnlyBalancesEnum[] = []
    if (shouldLoadBalances) result.push('leave_hours', 'bank_holiday_hours', 'contract_hours')

    return result
  }, [shouldLoadBalances])

  const employeeIds = useUnsafeMemo(
    () => paginatedShiftsRows.map((row) => row.id).filter(Boolean),
    [paginatedShiftsRows]
  )

  const { loading, data } = useQuery(PlanningBalancesDocument, {
    variables: {
      employeeIds,
      startAt: view.startAt,
      endAt: view.endAt,
      only,
    },
    skip: view.isByAreas || !only.length,
  })

  const { loading: loadingBalancesByPeriods, data: dataBalancesByPeriods } = useQuery(
    PlanningBalancesByPeriodsDocument,
    {
      variables: {
        employeeIds,
        periods: view.weeksInPeriod,
        only: ['leave_hours', 'bank_holiday_hours'],
      },
      skip: view.isByAreas || !canManageShifts || !hasOverPlannedAlertEnabled,
    }
  )

  const paginatedBalancesByPeriods = useUnsafeMemo(() => {
    const balancesByPeriods =
      dataBalancesByPeriods?.timePlanning.balancesByPeriodsConnection.nodes || []
    return balancesByPeriods.reduce(
      (acc: Record<string, TimePlanningBalancesByPeriod[]>, balance) => {
        acc[balance.employeeId] = [...(acc[balance.employeeId] || []), balance]
        return acc
      },
      {}
    )
  }, [dataBalancesByPeriods?.timePlanning.balancesByPeriodsConnection.nodes])

  const paginatedBalancesByEmployee = useUnsafeMemo(() => {
    const balances = data?.timePlanning.balancesConnection.nodes || []
    return balances.reduce((acc: Record<string, TimePlanningBalance>, balance) => {
      acc[balance.employeeId] = balance
      return acc
    }, {})
  }, [data?.timePlanning.balancesConnection.nodes])

  const [balancesByEmployee, setBalancesByEmployee] = useState<Record<string, TimePlanningBalance>>(
    {}
  )

  const [periodBalancesByEmployee, setPeriodBalancesByEmployee] = useState<
    Record<string, TimePlanningBalancesByPeriod[]>
  >({})

  useUnsafeEffect(() => {
    setPeriodBalancesByEmployee(paginatedBalancesByPeriods)
  }, [paginatedBalancesByPeriods])

  useUnsafeEffect(() => {
    setBalancesByEmployee({ ...balancesByEmployee, ...paginatedBalancesByEmployee })
  }, [paginatedBalancesByEmployee])

  useUnsafeEffect(() => {
    setBalancesByEmployee({})
    setPeriodBalancesByEmployee({})
  }, [view]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    loading: loading || loadingBalancesByPeriods,
    balancesByEmployee,
    periodBalancesByEmployee,
  }
}

export default useFetchPlanningBalances
